'use client'

import React, { useRef } from 'react'
import { CircleX, Search, X } from 'lucide-react'

import { Input, useFilter } from '@rewardops/ui'

import ResponsiveComponent from '~/components/responsive-component'
import { useTranslation } from '~/app/i18n/client'

type SearchBarProps = {
  selector?: React.ReactNode
  searchQuery: string
  onChange: (queryParams: { query: string; qType: string }) => void
  onClose: () => void
  selectorDisabled?: boolean
}

export function SearchBar({
  selector,
  searchQuery = '',
  onChange = () => {},
  onClose = () => {},
  selectorDisabled = false,
}: SearchBarProps) {
  const { t } = useTranslation()
  const { searchType } = useFilter()

  const inputRef = useRef<HTMLInputElement>(null)

  // Prevent default form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
  }

  // Clear input and focus
  const handleClear = () => {
    onClose()
    inputRef.current?.focus() // Keep focus on the input field
  }

  const desktopComponent = (
    <>
      <form onSubmit={handleSubmit}>
        <Search className="absolute left-3 top-3 h-4 w-4" />
        <Input
          ref={inputRef} // Attach the ref to the input
          name="search-input"
          type="text"
          placeholder={t('nav.search')}
          autoComplete="off"
          className={
            selectorDisabled
              ? 'rounded-3xl pl-10 text-foreground'
              : 'rounded-3xl rounded-r-md pl-10 text-foreground'
          }
          value={searchQuery}
          onChange={(e) =>
            onChange({
              query: e.target.value,
              qType: searchType,
            })
          }
        />
      </form>
      {searchQuery && (
        <button
          onClick={() => handleClear()}
          className={`absolute bottom-2 ${
            selectorDisabled ? 'right-4' : 'right-28'
          } text-foreground opacity-70 transition-opacity hover:opacity-100`}
        >
          <CircleX className="h-6 w-6 hover:text-tenant-primary-color" />
        </button>
      )}

      {/* Selector Section */}
      {selector && !selectorDisabled && (
        <div className="text-grey-300 absolute bottom-0 right-0 font-medium hover:opacity-100">
          {selector}
        </div>
      )}
    </>
  )

  const mobileComponent = (
    <>
      <form onSubmit={handleSubmit}>
        <Search className="absolute left-3 top-3 h-4 w-4" />
        <Input
          name="search-input"
          type="text"
          placeholder={t('nav.search')}
          className={`pl-10 text-foreground focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 ${
            selectorDisabled ? 'rounded-3xl' : ''
          }`}
          value={searchQuery}
          onChange={(e) =>
            onChange({
              query: e.target.value,
              qType: searchType,
            })
          }
        />
      </form>

      <button
        onClick={() => onClose()}
        className={`absolute bottom-3 ${
          selectorDisabled ? 'right-4' : 'right-28'
        } text-foreground opacity-70 transition-opacity hover:opacity-100`}
      >
        <X className="h-4 w-4" />
      </button>

      {/* Selector Section */}
      {selector && !selectorDisabled && (
        <div className="text-grey-300 absolute bottom-0 right-0 font-medium hover:opacity-100">
          {selector}
        </div>
      )}
    </>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}
